import React, { useState } from "react";
import {
  FaHandshake,
  FaRocket,
  FaChartLine,
  FaHeadset,
  FaCogs,
  FaHotel,
  FaUsers,
  FaGlobe,
  FaCheckCircle,
  FaEnvelope,
  FaPhone,
  FaBuilding,
} from "react-icons/fa";
import { toast } from "react-toastify";
import generalApi from "../api/generalApi";

const PartnerWithUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    businessName: "",
    businessType: "",
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await generalApi.submitPartnerApplication(formData);
      if (result.success) {
        toast.success(result.message || "Thank you for your interest! We'll get back to you soon.");
        setFormData({ name: "", email: "", phone: "", businessName: "", businessType: "", message: "" });
      } else {
        toast.error(result.message || "Submission failed. Please try again.");
      }
    } catch {
      toast.error("Network error. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const benefits = [
    {
      icon: FaRocket,
      title: "Expand Your Reach",
      description:
        "Connect with thousands of customers across India and boost your visibility",
    },
    {
      icon: FaChartLine,
      title: "Grow Your Revenue",
      description:
        "Increase bookings and revenue with our powerful marketing tools",
    },
    {
      icon: FaHeadset,
      title: "Dedicated Support",
      description:
        "Get 24/7 partner support to help you succeed and grow your business",
    },
    {
      icon: FaCogs,
      title: "Easy Management",
      description:
        "Intuitive dashboard for seamless operations and booking management",
    },
  ];

  const partnerTypes = [
    {
      icon: FaHotel,
      title: "Hotels & Resorts",
      description: "List your properties and reach travelers nationwide",
    },
    {
      icon: FaUsers,
      title: "Travel Agents",
      description: "Expand your offerings with exclusive deals and packages",
    },
    {
      icon: FaGlobe,
      title: "Service Providers",
      description: "Partner with us to offer tours, activities, and more",
    },
  ];

  const steps = [
    "Fill out the partnership form",
    "Our team reviews your application",
    "Complete the onboarding process",
    "Start growing your business with us",
  ];

  return (
    <div
      className="min-h-screen bg-white"
      style={{
        fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      }}
    >
      {/* Hero Section */}
      <header className="relative h-[250px] sm:h-[300px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1521737711867-e3b97375f902"
          alt="Partnership"
          className="w-full h-full object-cover"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(135deg, rgba(238, 46, 36, 0.85) 0%, rgba(255, 196, 0, 0.75) 100%)",
          }}
        />
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white px-4 py-6">
          <div
            className="text-xs sm:text-sm"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              background: "rgba(255, 255, 255, 0.2)",
              padding: "5px 12px",
              borderRadius: "20px",
              marginBottom: "12px",
              fontSize: "0.75rem",
              fontWeight: "600",
            }}
          >
            <FaHandshake className="text-sm" style={{ fontSize: "0.85rem" }} />
            <span className="whitespace-nowrap">Partnership Program</span>
          </div>
          <h1
            className="text-2xl sm:text-3xl md:text-4xl font-bold mb-2 sm:mb-3 text-center px-2"
            style={{
              letterSpacing: "-0.8px",
              textShadow: "0 2px 20px rgba(0,0,0,0.3)",
              fontWeight: "800",
              lineHeight: "1.2",
            }}
          >
            Partner With Trivana Hospitality
          </h1>
          <p
            className="text-sm sm:text-base md:text-lg text-center max-w-2xl px-4"
            style={{
              opacity: 0.95,
              fontWeight: "500",
              textShadow: "0 1px 10px rgba(0,0,0,0.2)",
              lineHeight: "1.5",
            }}
          >
            Join our network and grow your business with India's trusted
            hospitality platform
          </p>
        </div>
      </header>

      {/* Benefits Section */}
      <section className="container mx-auto px-4 py-8 sm:py-12">
        <h2
          className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 sm:mb-8 text-center px-2"
          style={{
            color: "#EE2E24",
            letterSpacing: "-0.8px",
            fontWeight: "800",
          }}
        >
          Why Partner With Us?
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl mx-auto">
          {benefits.map((benefit, index) => {
            const IconComponent = benefit.icon;
            return (
              <div
                key={index}
                className="bg-white rounded-lg p-4 sm:p-6 transform transition-all duration-300"
                style={{
                  boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                  border: "1px solid rgba(238, 46, 36, 0.1)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "translateY(-8px)";
                  e.currentTarget.style.boxShadow =
                    "0 12px 30px rgba(238, 46, 36, 0.15)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow =
                    "0 4px 15px rgba(0,0,0,0.08)";
                }}
              >
                <div
                  className="flex items-center justify-center rounded-full mb-3 sm:mb-4 mx-auto w-12 h-12 sm:w-[60px] sm:h-[60px]"
                  style={{
                    background:
                      "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                    boxShadow: "0 4px 12px rgba(238, 46, 36, 0.25)",
                  }}
                >
                  <IconComponent
                    className="text-lg sm:text-2xl"
                    style={{ color: "white" }}
                  />
                </div>
                <h3
                  className="font-bold mb-2 text-center text-base sm:text-lg"
                  style={{
                    color: "#2E2E2E",
                    fontWeight: "800",
                    letterSpacing: "-0.5px",
                  }}
                >
                  {benefit.title}
                </h3>
                <p
                  className="text-center text-xs sm:text-sm"
                  style={{
                    color: "#64748B",
                    lineHeight: "1.6",
                  }}
                >
                  {benefit.description}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Partner Types Section */}
      <section
        className="py-8 sm:py-12"
        style={{
          background:
            "linear-gradient(135deg, rgba(238, 46, 36, 0.03) 0%, rgba(255, 196, 0, 0.03) 100%)",
        }}
      >
        <div className="container mx-auto px-4">
          <h2
            className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 sm:mb-8 text-center px-2"
            style={{
              color: "#EE2E24",
              letterSpacing: "-0.8px",
              fontWeight: "800",
            }}
          >
            Who Can Partner With Us?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 max-w-5xl mx-auto">
            {partnerTypes.map((type, index) => {
              const IconComponent = type.icon;
              return (
                <div
                  key={index}
                  className="bg-white rounded-lg p-4 sm:p-6 text-center transform transition-all duration-300"
                  style={{
                    boxShadow: "0 4px 15px rgba(0,0,0,0.08)",
                    border: "1px solid rgba(238, 46, 36, 0.1)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-8px)";
                    e.currentTarget.style.boxShadow =
                      "0 12px 30px rgba(238, 46, 36, 0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 15px rgba(0,0,0,0.08)";
                  }}
                >
                  <div
                    className="inline-flex items-center justify-center rounded-full mb-3 sm:mb-4 w-14 h-14 sm:w-[70px] sm:h-[70px]"
                    style={{
                      background:
                        "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                      boxShadow: "0 4px 12px rgba(238, 46, 36, 0.25)",
                    }}
                  >
                    <IconComponent
                      className="text-xl sm:text-3xl"
                      style={{ color: "white" }}
                    />
                  </div>
                  <h3
                    className="font-bold mb-2 text-base sm:text-lg"
                    style={{
                      color: "#2E2E2E",
                      fontWeight: "800",
                      letterSpacing: "-0.5px",
                    }}
                  >
                    {type.title}
                  </h3>
                  <p
                    className="text-sm sm:text-base"
                    style={{
                      color: "#64748B",
                      lineHeight: "1.6",
                    }}
                  >
                    {type.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="container mx-auto px-4 py-8 sm:py-12">
        <h2
          className="text-xl sm:text-2xl md:text-3xl font-bold mb-6 sm:mb-8 text-center px-2"
          style={{
            color: "#EE2E24",
            letterSpacing: "-0.8px",
            fontWeight: "800",
          }}
        >
          How It Works
        </h2>
        <div className="max-w-3xl mx-auto">
          <div className="space-y-3 sm:space-y-4">
            {steps.map((step, index) => (
              <div
                key={index}
                className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-white rounded-lg"
                style={{
                  boxShadow: "0 3px 12px rgba(0,0,0,0.06)",
                  border: "1px solid rgba(238, 46, 36, 0.1)",
                }}
              >
                <div
                  className="flex-shrink-0 flex items-center justify-center rounded-full w-10 h-10 sm:w-11 sm:h-11"
                  style={{
                    background:
                      "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
                    boxShadow: "0 3px 10px rgba(238, 46, 36, 0.25)",
                  }}
                >
                  <span
                    className="text-base sm:text-lg"
                    style={{
                      color: "white",
                      fontWeight: "800",
                    }}
                  >
                    {index + 1}
                  </span>
                </div>
                <p
                  className="text-sm sm:text-base"
                  style={{
                    color: "#2E2E2E",
                    fontWeight: "600",
                    margin: 0,
                    lineHeight: "1.5",
                  }}
                >
                  {step}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section
        className="py-8 sm:py-12"
        style={{
          background:
            "linear-gradient(135deg, rgba(238, 46, 36, 0.03) 0%, rgba(255, 196, 0, 0.03) 100%)",
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto">
            <div
              className="bg-white rounded-lg p-5 sm:p-8"
              style={{
                boxShadow: "0 4px 20px rgba(238, 46, 36, 0.1)",
                border: "1px solid rgba(238, 46, 36, 0.1)",
              }}
            >
              <div className="text-center mb-5 sm:mb-6">
                <h2
                  className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3 px-2"
                  style={{
                    color: "#EE2E24",
                    letterSpacing: "-0.8px",
                    fontWeight: "800",
                  }}
                >
                  Start Your Partnership Journey
                </h2>
                <p
                  className="text-sm sm:text-base px-2"
                  style={{ color: "#64748B" }}
                >
                  Fill out the form below and our team will get in touch with
                  you shortly
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label
                      className="text-sm sm:text-base"
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        color: "#2E2E2E",
                        fontWeight: "600",
                      }}
                    >
                      Your Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      required
                      className="text-sm sm:text-base"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "2px solid rgba(238, 46, 36, 0.2)",
                        fontFamily: "'Inter', sans-serif",
                        transition: "all 0.3s ease",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#EE2E24")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = "rgba(238, 46, 36, 0.2)")
                      }
                    />
                  </div>
                  <div>
                    <label
                      className="text-sm sm:text-base"
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        color: "#2E2E2E",
                        fontWeight: "600",
                      }}
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="your.email@example.com"
                      required
                      className="text-sm sm:text-base"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "2px solid rgba(238, 46, 36, 0.2)",
                        fontFamily: "'Inter', sans-serif",
                        transition: "all 0.3s ease",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#EE2E24")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = "rgba(238, 46, 36, 0.2)")
                      }
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <label
                      className="text-sm sm:text-base"
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        color: "#2E2E2E",
                        fontWeight: "600",
                      }}
                    >
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      required
                      className="text-sm sm:text-base"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "2px solid rgba(238, 46, 36, 0.2)",
                        fontFamily: "'Inter', sans-serif",
                        transition: "all 0.3s ease",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#EE2E24")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = "rgba(238, 46, 36, 0.2)")
                      }
                    />
                  </div>
                  <div>
                    <label
                      className="text-sm sm:text-base"
                      style={{
                        display: "block",
                        marginBottom: "6px",
                        color: "#2E2E2E",
                        fontWeight: "600",
                      }}
                    >
                      Business Type *
                    </label>
                    <select
                      name="businessType"
                      value={formData.businessType}
                      onChange={handleChange}
                      required
                      className="text-sm sm:text-base"
                      style={{
                        width: "100%",
                        padding: "10px 12px",
                        borderRadius: "8px",
                        border: "2px solid rgba(238, 46, 36, 0.2)",
                        fontFamily: "'Inter', sans-serif",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                      }}
                      onFocus={(e) => (e.target.style.borderColor = "#EE2E24")}
                      onBlur={(e) =>
                        (e.target.style.borderColor = "rgba(238, 46, 36, 0.2)")
                      }
                    >
                      <option value="">Select business type</option>
                      <option value="hotel">Hotel / Resort</option>
                      <option value="travel-agent">Travel Agent</option>
                      <option value="service-provider">Service Provider</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label
                    className="text-sm sm:text-base"
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      color: "#2E2E2E",
                      fontWeight: "600",
                    }}
                  >
                    Business Name *
                  </label>
                  <input
                    type="text"
                    name="businessName"
                    value={formData.businessName}
                    onChange={handleChange}
                    placeholder="Your business or company name"
                    required
                    className="text-sm sm:text-base"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "2px solid rgba(238, 46, 36, 0.2)",
                      fontFamily: "'Inter', sans-serif",
                      transition: "all 0.3s ease",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#EE2E24")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = "rgba(238, 46, 36, 0.2)")
                    }
                  />
                </div>

                <div>
                  <label
                    className="text-sm sm:text-base"
                    style={{
                      display: "block",
                      marginBottom: "6px",
                      color: "#2E2E2E",
                      fontWeight: "600",
                    }}
                  >
                    Tell Us About Your Business *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Share details about your business, services, and why you'd like to partner with us..."
                    rows={4}
                    required
                    className="text-sm sm:text-base"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      borderRadius: "8px",
                      border: "2px solid rgba(238, 46, 36, 0.2)",
                      fontFamily: "'Inter', sans-serif",
                      transition: "all 0.3s ease",
                      resize: "vertical",
                    }}
                    onFocus={(e) => (e.target.style.borderColor = "#EE2E24")}
                    onBlur={(e) =>
                      (e.target.style.borderColor = "rgba(238, 46, 36, 0.2)")
                    }
                  />
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-3 rounded-lg font-bold transition-all duration-300"
                  style={{
                    background: submitting ? "#ccc" : "#EE2E24",
                    color: "white",
                    fontSize: "1rem",
                    fontWeight: "700",
                    letterSpacing: "-0.3px",
                    boxShadow: "0 4px 12px rgba(238, 46, 36, 0.3)",
                    border: "none",
                    cursor: submitting ? "not-allowed" : "pointer",
                  }}
                  onMouseEnter={(e) => {
                    if (!submitting) {
                      e.target.style.background = "#D82820";
                      e.target.style.transform = "translateY(-2px)";
                      e.target.style.boxShadow = "0 6px 16px rgba(238, 46, 36, 0.4)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!submitting) {
                      e.target.style.background = "#EE2E24";
                      e.target.style.transform = "translateY(0)";
                      e.target.style.boxShadow = "0 4px 12px rgba(238, 46, 36, 0.3)";
                    }
                  }}
                >
                  {submitting ? "Submitting..." : "Submit Partnership Request"}
                </button>
              </form>

              <div
                className="mt-6 pt-6"
                style={{ borderTop: "1px solid rgba(238, 46, 36, 0.1)" }}
              >
                <p
                  className="text-center mb-3 text-xs sm:text-sm"
                  style={{
                    color: "#64748B",
                    fontWeight: "600",
                  }}
                >
                  Or reach out to us directly:
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                  <a
                    href="mailto:booktrivana@gmail.com"
                    className="flex items-center gap-2 text-sm sm:text-base"
                    style={{
                      color: "#EE2E24",
                      textDecoration: "none",
                      fontWeight: "600",
                    }}
                  >
                    <FaEnvelope />
                    <span className="break-all">booktrivana@gmail.com</span>
                  </a>
                  <span
                    className="hidden sm:inline"
                    style={{ color: "#CBD5E1" }}
                  >
                    |
                  </span>
                  <a
                    href="tel:+911234567890"
                    className="flex items-center gap-2 text-sm sm:text-base"
                    style={{
                      color: "#EE2E24",
                      textDecoration: "none",
                      fontWeight: "600",
                    }}
                  >
                    <FaPhone />
                    +91 9257734350
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="text-white py-8 sm:py-12"
        style={{
          background: "linear-gradient(135deg, #EE2E24 0%, #FFC400 100%)",
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2
            className="text-xl sm:text-2xl md:text-3xl font-bold mb-2 sm:mb-3 px-2"
            style={{
              letterSpacing: "-0.8px",
              fontWeight: "800",
              textShadow: "0 2px 10px rgba(0,0,0,0.2)",
            }}
          >
            Ready to Grow Together?
          </h2>
          <p
            className="mb-4 sm:mb-6 text-sm sm:text-base px-4"
            style={{
              opacity: 0.95,
              fontWeight: "500",
            }}
          >
            Join hundreds of successful partners already working with Trivana
            Hospitality
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4 justify-center items-center">
            <div
              className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <FaCheckCircle className="text-sm sm:text-base" />
              <span
                className="text-xs sm:text-sm"
                style={{ fontWeight: "600" }}
              >
                20+ Partners
              </span>
            </div>
            <div
              className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <FaCheckCircle className="text-sm sm:text-base" />
              <span
                className="text-xs sm:text-sm"
                style={{ fontWeight: "600" }}
              >
                1M+ Bookings
              </span>
            </div>
            <div
              className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg"
              style={{ background: "rgba(255, 255, 255, 0.2)" }}
            >
              <FaCheckCircle className="text-sm sm:text-base" />
              <span
                className="text-xs sm:text-sm"
                style={{ fontWeight: "600" }}
              >
                24/7 Support
              </span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PartnerWithUs;
